/**
 * Small, relevant, animated SVG illustrations for each section. Pure SVG + the
 * shared .art-* / .sc-* CSS animation classes (in tailwind.css). Reduced-motion
 * is handled globally.
 */

function Gear({ size, color }) {
  const teeth = [];
  for (let i = 0; i < 8; i++) {
    teeth.push(
      <rect key={i} x={-3.5} y={-(size + 6)} width={7} height={9} rx={2} fill={color} transform={`rotate(${i * 45})`} />
    );
  }
  return (
    <g>
      {teeth}
      <circle r={size} fill={color} />
      <circle r={size * 0.42} fill="#0a0a14" />
    </g>
  );
}

const Star = ({ x, y, s = 7, c = "#fbbf24", d = 0 }) => (
  <path className="art-pulse" style={{ animationDelay: `${d}s` }} transform={`translate(${x} ${y})`} d={`M0 ${-s} L${s * 0.3} ${-s * 0.3} L${s} 0 L${s * 0.3} ${s * 0.3} L0 ${s} L${-s * 0.3} ${s * 0.3} L${-s} 0 L${-s * 0.3} ${-s * 0.3} Z`} fill={c} />
);

function About() {
  return (
    <g>
      <defs>
        <linearGradient id="art-hood" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#8b5cf6" /><stop offset="1" stopColor="#5b54e0" /></linearGradient>
      </defs>
      <circle cx="100" cy="90" r="64" fill="none" stroke="#8b5cf6" strokeOpacity="0.25" strokeWidth="1.5" strokeDasharray="4 7" className="sc-ring" />
      <g className="art-spin">
        <circle cx="100" cy="26" r="9" fill="#22d3ee" />
        <circle cx="164" cy="90" r="9" fill="#8b5cf6" />
        <circle cx="100" cy="154" r="9" fill="#ec4899" />
        <circle cx="36" cy="90" r="9" fill="#34d399" />
      </g>
      <circle cx="100" cy="90" r="42" fill="#15151f" stroke="#ffffff18" />
      <path d="M72 122 Q72 96 100 96 Q128 96 128 122 Z" fill="url(#art-hood)" />
      <circle cx="100" cy="80" r="16" fill="#d9a87e" />
      <ellipse cx="100" cy="71" rx="17" ry="10" fill="#16161f" />
    </g>
  );
}

function Toolkit() {
  return (
    <g>
      <g transform="translate(74 98)"><g className="art-spin"><Gear size={26} color="#8b5cf6" /></g></g>
      <g transform="translate(130 64)"><g className="art-spin-rev"><Gear size={17} color="#22d3ee" /></g></g>
      <g className="art-float" transform="translate(124 124)">
        <rect x="-32" y="-19" width="64" height="38" rx="9" fill="#15151f" stroke="#ffffff18" />
        <text x="0" y="6" textAnchor="middle" fontSize="19" fontWeight="700" fill="#34d399" fontFamily="'Consolas',monospace">&lt;/&gt;</text>
      </g>
    </g>
  );
}

function Work() {
  return (
    <g>
      <g className="art-float">
        <rect x="22" y="42" width="124" height="94" rx="11" fill="#15151f" stroke="#ffffff18" />
        <path d="M22 53 a11 11 0 0 1 11 -11 h102 a11 11 0 0 1 11 11 v8 h-124 Z" fill="#1f1f2e" />
        <circle cx="34" cy="52" r="2.6" fill="#ff5f57" /><circle cx="44" cy="52" r="2.6" fill="#febc2e" /><circle cx="54" cy="52" r="2.6" fill="#28c840" />
        <rect x="34" y="74" width="62" height="7" rx="3.5" fill="#8b5cf6" className="art-bar" style={{ animationDelay: "0s" }} />
        <rect x="34" y="88" width="50" height="7" rx="3.5" fill="#22d3ee" className="art-bar" style={{ animationDelay: "0.4s" }} />
        <rect x="34" y="102" width="56" height="7" rx="3.5" fill="#ec4899" className="art-bar" style={{ animationDelay: "0.8s" }} />
        <rect x="104" y="74" width="30" height="46" rx="5" fill="#22d3ee" opacity="0.25" />
      </g>
      <g className="art-float-2" transform="translate(150 68)">
        <rect x="0" y="0" width="38" height="66" rx="9" fill="#15151f" stroke="#ffffff18" />
        <rect x="5" y="9" width="28" height="40" rx="4" fill="#1f1f2e" />
        <rect x="9" y="15" width="20" height="4" rx="2" fill="#8b5cf6" className="art-bar" style={{ animationDelay: "0.2s" }} />
        <rect x="9" y="23" width="16" height="4" rx="2" fill="#22d3ee" className="art-bar" style={{ animationDelay: "0.6s" }} />
        <circle cx="19" cy="57" r="3" fill="#3a3a52" />
      </g>
    </g>
  );
}

function Education() {
  return (
    <g>
      <defs>
        <linearGradient id="art-cap" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#8b5cf6" /><stop offset="1" stopColor="#6d28d9" /></linearGradient>
      </defs>
      {/* book */}
      <g transform="translate(100 124)">
        <rect x="-46" y="-8" width="92" height="32" rx="5" fill="#1b1b2a" stroke="#ffffff18" />
        <rect x="-2" y="-8" width="4" height="32" fill="#0a0a14" />
        <rect x="-40" y="-1" width="34" height="4" rx="2" fill="#34d399" />
        <rect x="-40" y="8" width="28" height="4" rx="2" fill="#475069" />
        <rect x="8" y="-1" width="34" height="4" rx="2" fill="#22d3ee" />
        <rect x="8" y="8" width="26" height="4" rx="2" fill="#475069" />
      </g>
      {/* graduation cap */}
      <g className="art-float" transform="translate(100 68)">
        <path d="M-24 12 L-24 30 Q0 44 24 30 L24 12" fill="#5b21b6" />
        <path d="M0 -18 L48 2 L0 22 L-48 2 Z" fill="url(#art-cap)" />
        <circle cx="0" cy="2" r="5" fill="#1b1b2a" />
        <line x1="48" y1="2" x2="48" y2="30" stroke="#fbbf24" strokeWidth="2.5" />
        <circle cx="48" cy="33" r="4.5" fill="#fbbf24" className="art-float-2" />
      </g>
      <Star x={40} y={44} s={7} d={0} />
      <Star x={160} y={52} s={6} c="#22d3ee" d={0.6} />
      <Star x={150} y={120} s={5} c="#ec4899" d={1.1} />
    </g>
  );
}

function Services() {
  const icon = (k) => {
    const s = { stroke: "#fff", strokeWidth: 2.4, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };
    if (k === "pen") return (<g {...s}><path d="M-7 7 L-7 2 L4 -9 L9 -4 L-2 7 Z" /><path d="M2 -7 L7 -2" /></g>);
    if (k === "code") return (<g {...s}><path d="M-3 -6 L-9 0 L-3 6" /><path d="M3 -6 L9 0 L3 6" /></g>);
    return (<g {...s}><rect x="-8" y="-7" width="16" height="14" rx="2" /><path d="M-4 -2 L4 -2 M-4 2 L2 2" /></g>);
  };
  return (
    <g>
      <line x1="60" y1="74" x2="128" y2="62" stroke="#ffffff22" strokeWidth="1.5" strokeDasharray="2 5" />
      <line x1="128" y1="62" x2="96" y2="120" stroke="#ffffff22" strokeWidth="1.5" strokeDasharray="2 5" />
      <line x1="96" y1="120" x2="60" y2="74" stroke="#ffffff22" strokeWidth="1.5" strokeDasharray="2 5" />
      <g className="art-pulse" style={{ animationDelay: "0s" }} transform="translate(60 74)"><circle r="26" fill="#22d3ee" /><g>{icon("pen")}</g></g>
      <g className="art-pulse" style={{ animationDelay: "0.5s" }} transform="translate(128 62)"><circle r="26" fill="#8b5cf6" /><g>{icon("code")}</g></g>
      <g className="art-pulse" style={{ animationDelay: "1s" }} transform="translate(96 120)"><circle r="26" fill="#ec4899" /><g>{icon("doc")}</g></g>
    </g>
  );
}

const ART = { about: About, toolkit: Toolkit, work: Work, education: Education, services: Services };

export default function SectionArt({ type, className = "" }) {
  const Art = ART[type] || About;
  return (
    <div className={`pointer-events-none ${className}`} aria-hidden="true">
      <svg viewBox="0 0 200 180" className="h-full w-full overflow-visible">
        <Art />
      </svg>
    </div>
  );
}
